import {
  Box,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { BaseTheme, baseTheme, IconButton } from 'design-system';
import { ActionView as Action, ButtonView } from 'interfaces/graphqlViews';
import { DialogSectionView as DialogSectionType } from 'interfaces/graphqlViews';
import { ReactNode, useCallback, useState } from 'react';
import { MdClose, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { useClient } from 'urql';
import useIsViewport from 'hooks/useIsViewport';
import { SnackbarVariant, useSnackbar } from 'hooks/useSnackBar';
import downloadCSV from 'utils/download-csv';
import { TableDialogFragment } from 'generated/graphql';
import ActionButton from './ActionButton';

const dialogButtonSx = (theme: BaseTheme) => ({ maxWidth: 250, margin: theme.spacing(1, 'auto', 0) });

// A link the button follows itself; any other action is run by the dialog.
const isLinkButton = (button: ButtonView) => button.actions.length === 1 && button.actions[0].url.length > 0;

export enum TableDialogVariant {
  Default = 'Default',
  Detailed = 'Detailed', // Detailed = dialog contains centered graphic, title, subtitle and action buttons
}

const SectionContent = ({
  section,
  loadingTrigger,
  onClose,
  onQuery,
}: {
  section: DialogSectionType;
  loadingTrigger: string | null;
  onClose: () => void;

  onQuery: (query: string, variables?: string) => void;
}) => {
  const { showSnackbar } = useSnackbar();
  return (
    <>
      <Box component="ul" sx={{ paddingX: 0, paddingY: 1 }}>
        {section.rows.map((row, j) => (
          <Box
            component="li"
            key={`${row.label + j}`}
            sx={{
              listStyleType: 'disc',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>{row.label}:</span>
            <span>{row.value}</span>
          </Box>
        ))}
      </Box>
      {section.button && (
        <Box sx={{ textAlign: 'center' }}>
          <ActionButton
            {...section.button}
            size="medium"
            sx={dialogButtonSx}
            loading={loadingTrigger === section.button.actions?.[0]?.query}
            onClick={
              isLinkButton(section.button)
                ? undefined
                : () => {
                    if (section.button?.actions.length === 1) {
                      const action = section.button.actions[0];
                      if (action.query === 'BACK') {
                        onClose();
                        return;
                      }
                      if (action.query.length > 0) onQuery(action.query, action.variables);
                      return;
                    }

                    if (section.button?.actions.length === 0) {
                      onClose();
                      return;
                    }
                    showSnackbar({ message: 'Cannot handle multiple actions on a button' }, SnackbarVariant.ERROR);
                  }
            }
          />
        </Box>
      )}
    </>
  );
};

const CollapsableSection = ({ title, children }: { title: string; children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const handleClick = () => setOpen(!open);
  return (
    <>
      <ListItemButton sx={{ paddingX: 0 }} onClick={handleClick}>
        <ListItemText primary={title} />
        {open ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {children}
      </Collapse>
    </>
  );
};

const NestedSection = ({
  section,
  loadingTrigger,
  onClose,
  onQuery,
}: {
  section?: DialogSectionType;
  loadingTrigger: string | null;
  onClose: () => void;
  onQuery: (query: string, variables?: string) => void;
}) => {
  if (!section) return null;
  return (
    <CollapsableSection title={section.title}>
      <SectionContent section={section} loadingTrigger={loadingTrigger} onClose={onClose} onQuery={onQuery} />
      {section.groups?.map((subSection, index) => {
        if (!subSection) return null;
        return (
          <NestedSection
            key={index}
            section={subSection}
            loadingTrigger={loadingTrigger}
            onClose={onClose}
            onQuery={onQuery}
          />
        );
      })}
    </CollapsableSection>
  );
};

const DialogSection = ({
  section,
  loadingTrigger,
  onClose,
  onQuery,
}: {
  section: DialogSectionType;
  loadingTrigger: string | null;
  onClose: () => void;
  onQuery: (query: string, variables?: string) => void;
}) => {
  return (
    <ListItem
      sx={{
        padding: 0,
        display: 'list-item',
        backgroundColor: 'transparent',
        border: 'none',
      }}
    >
      <ListItemText primary={section.title} />
      {section.rows.length > 0 && (
        <SectionContent loadingTrigger={loadingTrigger} section={section} onClose={onClose} onQuery={onQuery} />
      )}
      {section.groups?.map((subSection, index) => {
        if (!subSection) return null;
        return (
          <NestedSection
            key={index}
            section={subSection}
            loadingTrigger={loadingTrigger}
            onClose={onClose}
            onQuery={onQuery}
          />
        );
      })}
    </ListItem>
  );
};

const DialogContents = ({
  data,
  variant,
  graphic,
  onClose,
}: {
  data: TableDialogFragment[];
  variant: TableDialogVariant;
  graphic?: ReactNode;
  onClose: (action?: Action) => void;
}) => {
  const [currentDialogIndex, setCurrentDialogIndex] = useState(0);
  const currentDialogData = data[currentDialogIndex];
  const { showSnackbar } = useSnackbar();
  const client = useClient();
  const [loadingTrigger, setLoadingTrigger] = useState<string | null>(null);
  const isDetailed = variant === TableDialogVariant.Detailed;
  const showGraphic = isDetailed && graphic;

  const handleNextDialog = useCallback(
    (action?: Action) => {
      if (currentDialogIndex < data.length - 1) {
        setCurrentDialogIndex((prev) => prev + 1);
      } else {
        onClose(action);
      }
    },
    [currentDialogIndex, data.length, onClose],
  );

  const handleQuery = useCallback(
    async (query: string, variables?: string, action?: Action) => {
      setLoadingTrigger(query);
      const parsedVariables = variables ? JSON.parse(variables) : undefined;
      const clientQueryMap = {
        mutation: async (query: any) => {
          return await client.mutation(query, parsedVariables).toPromise();
        },
        query: async (query: any) => {
          return await client.query(query, parsedVariables).toPromise();
        },
      };
      const result = query.startsWith('mutation')
        ? await clientQueryMap.mutation(query)
        : await clientQueryMap.query(query);
      setLoadingTrigger(null);
      if (result.error) {
        return;
      }
      if (result.data?.bulkPaymentExport) {
        try {
          downloadCSV(result.data?.bulkPaymentExport, `payment_export_${Date.now()}`);
        } catch (error) {
          alert(`Failed to download file: ${error}`);
        }
      } else if (result.data?.sellerLoadoutsExport) {
        try {
          downloadCSV(result.data?.sellerLoadoutsExport, `seller_loadouts_export_${Date.now()}`);
        } catch (error) {
          alert(`Failed to download file: ${error}`);
        }
      } else if (result.data?.TableGroupExport) {
        try {
          downloadCSV(result.data?.TableGroupExport, `table_group_export_${Date.now()}`);
        } catch (error) {
          alert(`Failed to download file: ${error}`);
        }
      }
      handleNextDialog(action);
    },
    [client, handleNextDialog],
  );

  return (
    <>
      {data.length > 1 && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: theme.spacing(2),
          })}
        >
          <Stepper
            activeStep={currentDialogIndex}
            alternativeLabel
            sx={{
              width: '100%',
              '.MuiStepLabel-label': {
                display: 'none', // hides the labels
              },
            }}
          >
            {data.map((_, index) => (
              <Step key={index}>
                <StepLabel />
              </Step>
            ))}
          </Stepper>
        </Box>
      )}
      <IconButton
        onClick={() => {
          onClose();
        }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
        }}
        track={false}
      >
        <MdClose size={24} fill={baseTheme.palette.grey[700]} />
      </IconButton>
      {showGraphic && <Box sx={{ overflow: 'hidden' }}>{graphic}</Box>}
      <DialogTitle
        sx={(theme) => ({
          marginBottom: 0,
          textAlign: isDetailed ? 'center' : 'initial',
          paddingTop: isDetailed ? 0 : theme.spacing(2),
        })}
      >
        {currentDialogData?.title}
      </DialogTitle>
      <DialogContent
        sx={
          isDetailed
            ? {
                flex: 'none',
                '& .MuiDialog-container': {
                  '& .MuiPaper-root': {
                    display: 'flex',
                    alignContent: 'center',
                    justifyContent: 'center',
                  },
                },
              }
            : { maxWidth: '500px' }
        }
      >
        <Typography
          sx={(theme) => ({
            marginY: theme.spacing(1),
            textAlign: isDetailed ? 'center' : 'initial',
            color: isDetailed ? theme.palette.grey[700] : 'initial',
          })}
        >
          {currentDialogData?.info}
        </Typography>
        <List
          sx={(theme) => ({
            marginTop: theme.spacing(-2),
            color: theme.palette.text.primary,
          })}
        >
          {currentDialogData?.sections.map((section, i) => (
            <DialogSection
              section={section}
              loadingTrigger={loadingTrigger}
              key={`${section.title + i}`}
              onClose={onClose}
              onQuery={handleQuery}
            />
          ))}
        </List>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {currentDialogData?.buttons.map((button, index) => (
            <Box key={index} sx={{ textAlign: 'center', minWidth: `calc(100%/2 - 1%)` }}>
              <ActionButton
                {...button}
                size="medium"
                sx={[dialogButtonSx, { minWidth: '100%' }]}
                loading={loadingTrigger === button.actions?.[0]?.query}
                onClick={
                  isLinkButton(button)
                    ? undefined
                    : () => {
                        if (button.actions.length === 1) {
                          const action = button.actions[0];
                          if (action.query === 'BACK') {
                            handleNextDialog(); // Should always handle next dialog if next dialog exists
                            return;
                          }
                          if (action.query.length > 0) handleQuery(action.query, action.variables, action);
                        } else {
                          showSnackbar({ message: 'Cannot handle multiple actions on button' }, SnackbarVariant.ERROR);
                        }
                      }
                }
              />
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </>
  );
};

const TableDialog = ({
  data,
  variant = TableDialogVariant.Default,
  graphic,
  onClose,
}: {
  data?: TableDialogFragment[];
  variant?: TableDialogVariant;
  graphic?: ReactNode;
  onClose: (action?: Action) => void;
}) => {
  const isMobile = useIsViewport();
  const isDetailed = variant === TableDialogVariant.Detailed;
  const dialogVariant = variant;

  const handleClose = () => onClose();

  if (data && data.length === 0) return null;

  return (
    <Dialog
      open={!!data}
      fullScreen={isDetailed && isMobile}
      onClose={handleClose}
      sx={
        isDetailed
          ? (theme) => ({
              '& .MuiDialog-container': {
                '& .MuiPaper-root': {
                  alignContent: 'center',
                  justifyContent: 'center',
                  [theme.breakpoints.down('md')]: {
                    borderRadius: 0,
                    position: 'relative',
                  },
                  [theme.breakpoints.up('md')]: {
                    width: 500,
                  },
                },
              },
            })
          : {}
      }
    >
      {data && <DialogContents data={data} variant={dialogVariant} graphic={graphic} onClose={onClose} />}
    </Dialog>
  );
};

export default TableDialog;
